/**
 * @author        nico@zumper.com (Nico Rattazzi)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   Bundle Reducer
 */

import { Bundle, type NonLocalUser, type RawBundle, type RawNonLocalUser } from '@zcode/bizos'
import { APPLY, TOUR } from '@zcode/nr/es/shared/constants/message'
import type { Listable, Property } from '@zcode/state-type'
import type { MaybeEmpty, Mutable } from '@zcode/utility-types'
import { filterActions } from '@zumper/redux-ignore'
import deepmerge from 'deepmerge'
import {
  ADD_TO_FAVORITES,
  CLEAR_BUNDLE,
  CONVERT_LOCAL_FAVORITES,
  ON_MESSAGE_SENT,
  RECEIVE_LOCALIZER_VALUES,
  REMOVE_FROM_FAVORITES,
  SET_BUNDLE,
  SET_USER,
  SET_VISITED,
} from '../actions/constants'

type ActionTypes =
  | {
      buildingId?: number | null
      listingId?: number | null
      timestamp: number
      type: typeof ADD_TO_FAVORITES
    }
  | {
      buildingId?: number | null
      listingId?: number | null
      type: typeof REMOVE_FROM_FAVORITES
    }
  | {
      buildingIds?: readonly number[]
      equalCTAIntent?: string
      listingIds?: readonly number[]
      success?: boolean
      timestamp: number
      type: typeof ON_MESSAGE_SENT
    }
  | { type: typeof SET_USER; user: NonLocalUser }
  | { bundle?: Bundle | RawBundle; type: typeof SET_BUNDLE }
  | { type: typeof CLEAR_BUNDLE }
  | { localFavorites: RawBundle['favorites']; type: typeof CONVERT_LOCAL_FAVORITES }
  | {
      payload?: { isGroup?: boolean } & Partial<
        Pick<Property | Listable, 'buildingId' | 'listingId' | 'paUrl' | 'uId'>
      >
      type: typeof SET_VISITED
    }
  | {
      payload: Pick<
        Partial<Bundle>,
        | 'equalApplyMessaged'
        | 'equalCheckAvailabilityMessaged'
        | 'equalTourMessaged'
        | 'favorites'
        | 'messaged'
        | 'user'
        | 'visited'
      >
      type: typeof RECEIVE_LOCALIZER_VALUES
    }

const whitelist = [
  ADD_TO_FAVORITES,
  REMOVE_FROM_FAVORITES,
  ON_MESSAGE_SENT,
  SET_USER,
  SET_BUNDLE,
  CLEAR_BUNDLE,
  SET_VISITED,
  RECEIVE_LOCALIZER_VALUES,
  CONVERT_LOCAL_FAVORITES,
]

const bundleReducer = function bundle(
  state = new Bundle(),
  action: MaybeEmpty<ActionTypes> = {}
): Bundle {
  switch (action.type) {
    case ADD_TO_FAVORITES: {
      const { buildingId, listingId, timestamp } = action

      if (buildingId && listingId) {
        return new Bundle({
          ...state._data,
          favorites: {
            ...state.favorites,
            buildings: {
              ...state.favorites.buildings,
              [buildingId.toString()]: timestamp,
            },
            listings: {
              ...state.favorites.listings,
              [listingId.toString()]: timestamp,
            },
          },
        })
      } else if (buildingId) {
        return new Bundle({
          ...state._data,
          favorites: {
            ...state.favorites,
            buildings: {
              ...state.favorites.buildings,
              [buildingId.toString()]: timestamp,
            },
          },
        })
      } else if (listingId) {
        return new Bundle({
          ...state._data,
          favorites: {
            ...state.favorites,
            listings: {
              ...state.favorites.listings,
              [listingId.toString()]: timestamp,
            },
          },
        })
      }
      return state
    }
    case REMOVE_FROM_FAVORITES: {
      const { buildingId, listingId } = action

      if (buildingId && listingId) {
        const {
          favorites: {
            buildings: { [`${buildingId}` as const]: _, ...newBuildings },
            listings: { [`${listingId}` as const]: _2, ...newListings },
            ...newFavorites
          },
        } = state
        return new Bundle({
          ...state._data,
          favorites: { ...newFavorites, buildings: newBuildings, listings: newListings },
        })
      } else if (buildingId) {
        const {
          favorites: {
            buildings: { [`${buildingId}` as const]: _, ...newBuildings },
            ...newFavorites
          },
        } = state
        return new Bundle({
          ...state._data,
          favorites: { ...newFavorites, buildings: newBuildings },
        })
      } else if (listingId) {
        const {
          favorites: {
            listings: { [`${listingId}` as const]: _, ...newListings },
            ...newFavorites
          },
        } = state
        return new Bundle({ ...state._data, favorites: { ...newFavorites, listings: newListings } })
      }
      return state
    }
    case ON_MESSAGE_SENT: {
      const {
        success = false,
        listingIds = [],
        buildingIds = [],
        equalCTAIntent,
        timestamp,
      } = action
      const listings: { [listingId: `${number}`]: number | undefined } = {}
      const buildings: { [buildingId: `${number}`]: number | undefined } = {}
      const newState: Partial<Mutable<RawBundle>> = {}

      if (success) {
        for (const buildingId of buildingIds) {
          buildings[`${buildingId}`] = timestamp
        }
        for (const listingId of listingIds) {
          listings[`${listingId}`] = timestamp
        }
        newState.messaged = { listings, buildings }

        if (equalCTAIntent === APPLY) {
          newState.equalApplyMessaged = { listings, buildings }
        }
        if (equalCTAIntent === TOUR) {
          newState.equalTourMessaged = { listings, buildings }
        }
        if (equalCTAIntent !== TOUR && equalCTAIntent !== APPLY) {
          newState.equalCheckAvailabilityMessaged = { listings, buildings }
        }

        return new Bundle(deepmerge(state._data, newState))
      }
      return state
    }
    case SET_USER: {
      const { user } = action
      return new Bundle({ ...state._data, user: user._data as RawNonLocalUser })
    }
    case SET_BUNDLE: {
      if (action.bundle) {
        // don't overwrite state dislikes or move in because bundle doesn't have them
        let newBundle = action.bundle instanceof Bundle ? action.bundle._data : action.bundle

        if (state.dislike) {
          newBundle = { ...newBundle, dislike: state.dislike }
        }
        if (state.visited) {
          newBundle = { ...newBundle, visited: state.visited }
        }

        // store messaged and favorited ids from both localStorage and bundleApi
        newBundle = {
          ...newBundle,
          favorites: {
            listings: action.bundle.user
              ? { ...newBundle.favorites?.listings }
              : { ...state.favorites.listings },
            buildings: action.bundle.user
              ? { ...newBundle.favorites?.buildings }
              : { ...state.favorites.buildings },
          },
          messaged: {
            listings: action.bundle.user
              ? { ...newBundle.messaged?.listings }
              : { ...state.messaged.listings },
            buildings: action.bundle.user
              ? { ...newBundle.messaged?.buildings }
              : { ...state.messaged.buildings },
          },
        }

        if (state.user?.tenantProfile?.moveIn && action.bundle.user) {
          const bundleUser =
            'toJSON' in action.bundle.user ? action.bundle.user.toJSON() : action.bundle.user
          newBundle = {
            ...newBundle,
            user: {
              ...bundleUser,
              tenant_profile: {
                ...bundleUser.tenant_profile,
                move_in: state.user.tenantProfile.moveIn,
              },
            },
          }
        }
        return new Bundle(newBundle)
      }
      return state
    }
    case CLEAR_BUNDLE: {
      return new Bundle()
    }
    case CONVERT_LOCAL_FAVORITES: {
      if (action.localFavorites) {
        return new Bundle(
          deepmerge<RawBundle>(state._data, {
            favorites: action.localFavorites,
          })
        )
      }
      return state
    }
    case SET_VISITED: {
      const { payload } = action
      if (payload) {
        const { uId, buildingId, listingId, paUrl, isGroup } = payload
        let newVisited: RawBundle['visited']
        if (isGroup && paUrl) {
          newVisited = { ...(newVisited ?? state._data.visited ?? {}), [paUrl]: true }
        }
        if (uId) {
          newVisited = { ...(newVisited ?? state._data.visited ?? {}), [uId]: true }
        }
        if (buildingId) {
          newVisited = { ...(newVisited ?? state._data.visited ?? {}), [`b-${buildingId}`]: true }
        }
        if (listingId) {
          newVisited = { ...(newVisited ?? state._data.visited ?? {}), [`l-${listingId}`]: true }
        }
        return newVisited ? new Bundle({ ...state._data, visited: newVisited }) : state
      }
      return state
    }
    case RECEIVE_LOCALIZER_VALUES: {
      const { payload } = action
      const {
        equalApplyMessaged,
        equalCheckAvailabilityMessaged,
        equalTourMessaged,
        favorites,
        messaged,
        user,
        visited,
      } = payload

      let newState: RawBundle | undefined
      if (equalApplyMessaged) {
        newState = deepmerge<RawBundle>(newState ?? state._data, {
          equalApplyMessaged,
        })
      }
      if (equalTourMessaged) {
        newState = deepmerge<RawBundle>(newState ?? state._data, {
          equalTourMessaged,
        })
      }
      if (equalCheckAvailabilityMessaged) {
        newState = deepmerge<RawBundle>(newState ?? state._data, {
          equalCheckAvailabilityMessaged,
        })
      }
      if (favorites) {
        newState = deepmerge<RawBundle>(newState ?? state._data, {
          favorites,
        })
      }
      if (messaged) {
        newState = deepmerge<RawBundle>(newState ?? state._data, {
          messaged,
        })
      }
      if (visited) {
        newState = deepmerge<RawBundle>(newState ?? state._data, {
          visited,
        })
      }
      if (user?.tenantProfile?.moveIn && state.user) {
        const newStateUser = state.user.toJSON()
        newState = {
          ...(newState ?? state._data),
          user: {
            ...newStateUser,
            tenant_profile: { ...newStateUser.tenant_profile, move_in: user.tenantProfile.moveIn },
          },
        }
      }
      return newState ? new Bundle(newState) : state
    }
    default:
      return state
  }
}

// eslint-disable-next-line import/no-default-export
export default filterActions(bundleReducer, whitelist)
